import { launchedInTelegram, telegramApp } from "./telegram";

/**
 * Platform adapter (docs/MOBILE_UI_PLAN.html D42): the browser and the Telegram Mini App behave differently for
 * sharing, external links and haptics. Features call these functions and never `navigator.share`, `window.open` or
 * `window.Telegram` directly, so a new host means a new adapter, not a refactor.
 */
export { launchedInTelegram, loadTelegram, telegramApp, useTelegramApp } from "./telegram";

export type ShareResult = "shared" | "copied" | "failed";

/**
 * The address of a page of the store that is safe to give to someone else: origin + path + query, without the hash
 * (Telegram's signed launch data travels there) and without an open sheet.
 */
export function shareableUrl(path: string, search = ""): string {
  const params = new URLSearchParams(search);
  params.delete("sheet");
  const query = params.toString();
  return `${window.location.origin}${path}${query ? `?${query}` : ""}`;
}

/** Shares a link (build it with `shareableUrl`): the system share sheet where there is one, else the clipboard. */
export async function share({ title, url }: { title: string; url: string }): Promise<ShareResult> {
  try {
    if (typeof navigator.share === "function") {
      await navigator.share({ title, url });
      return "shared";
    }
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch {
    // the customer closed the share sheet, or the clipboard is unavailable
    return "failed";
  }
}

/** Opens a page of another site (a payment provider, a document) outside the store. */
export function openExternal(url: string): void {
  const app = telegramApp();
  if (app) {
    app.openLink(url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/** A short haptic tap. Only inside Telegram: the web Vibration API feels cheap and iOS lacks it (D38). */
export function haptic(kind: "tap" | "success" | "error"): void {
  const feedback = telegramApp()?.HapticFeedback;
  if (!feedback) return;
  if (kind === "tap") feedback.impactOccurred("light");
  else feedback.notificationOccurred(kind);
}

/**
 * The Mini App's signed launch data (`initData`), which the platform checks with the bot token to sign the customer
 * in (ADR-0009 in the platform). Undefined on the web or before Telegram's script has loaded.
 */
export function telegramInitData(): string | undefined {
  const initData = telegramApp()?.initData;
  return initData === "" ? undefined : initData;
}

/** True when the store runs as the Telegram Mini App. */
export function isTelegram(): boolean {
  return launchedInTelegram;
}

/**
 * Leaves the store for a page that sends the customer back (MyID's check, ADR-0013 of the platform): a full
 * navigation in the same tab, also inside the Telegram Mini App, so the return address opens here again.
 */
export function leaveFor(url: string): void {
  window.location.assign(url);
}
